import {
  faBold,
  faHighlighter,
  faItalic,
  faLink,
  faPencil,
  faStrikethrough,
  faSubscript,
  faSuperscript,
  faUnderline,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useState } from "react";
import { GrigoraDashboardContext } from "../dashboard/context";
import { GrigoraFormatsContext } from "../formats";
import { Popover, PopoverAnchor, PopoverContent } from "./popover.tsx";
import Panel from "./panel";
import "./richtext-formatting.scss";
import GlobalSketchPicker from "./globalsketchpicker";
import Tabs from "./tabs";
import { isMultipleRichTextSelected } from "../dom";
import { useNode } from "@craftjs/core";
import TextInput from "./text";
import ToggleInput from "./toggle";
import { INPUT_LIMITS } from "../input-limits";

function FormattingItem({ format, icon, ...props }) {
  const { activeFormats, applyFormat, removeFormat } = useContext(
    GrigoraFormatsContext,
  );

  return (
    <div
      className={`richtext-setting-item ${
        activeFormats.includes(format) ? "active" : ""
      } w-[30px] h-[30px] p-1 flex items-center justify-center`}
      onClick={(e) => {
        if (activeFormats.includes(format)) {
          removeFormat(format);
        } else {
          applyFormat(format);
        }
      }}
      title={format}
    >
      <FontAwesomeIcon icon={icon} size="xs" />
    </div>
  );
}

function ColorFormattingItem({ format, icon, ...props }) {
  const {
    activeFormats,
    applyFormat,
    getFormatProps,
    removeFormat,
    updateFormat,
  } = useContext(GrigoraFormatsContext);
  const [colorModalVisible, setColorModalVisible] = useState(false);
  const [color, setColor] = useState(false);

  function applyUpdateFormat(format, formatProps) {
    if (activeFormats.includes(format)) {
      updateFormat(format, formatProps);
    } else {
      applyFormat(format, formatProps);
    }
  }

  function TextSettings() {
    return (
      <div className="mt-2">
        <GlobalSketchPicker
          value={color?.color}
          onChange={(newColor) => {
            const newFormatProps = {
              ...color,
              color: newColor,
            };
            applyUpdateFormat(format, newFormatProps);
            setColor(newFormatProps);
          }}
        />
      </div>
    );
  }

  function BackgroundSettings() {
    return (
      <div className="mt-2">
        <GlobalSketchPicker
          value={color?.backgroundColor}
          onChange={(newColor) => {
            const newFormatProps = {
              ...color,
              backgroundColor: newColor,
            };
            applyUpdateFormat(format, newFormatProps);
            setColor(newFormatProps);
          }}
        />
      </div>
    );
  }

  const tabs = [
    {
      label: "Text",
      key: "text",
      children: TextSettings(),
    },
    {
      label: "Background",
      key: "background",
      children: BackgroundSettings(),
    },
  ];

  return (
    <Popover
      open={colorModalVisible}
      onOpenChange={setColorModalVisible}
      placement="left"
    >
      <PopoverAnchor
        onClick={() => {
          if (!colorModalVisible) {
            setColor(getFormatProps(format));
          }
          setColorModalVisible(!colorModalVisible);
        }}
        title={format}
        className={`richtext-setting-item ${
          activeFormats.includes(format) ? "active" : ""
        } w-[30px] h-[30px] p-1 flex items-center justify-center`}
      >
        <FontAwesomeIcon icon={icon} size="xs" />
      </PopoverAnchor>
      <PopoverContent className="!bg-editor-light-main !z-[1000] !shadow-editor-floating-elements !rounded-[10px] p-4 py-2 pb-4">
        <Tabs
          type="richtext-setting"
          items={tabs.map((item, i) => {
            return {
              label: item.label,
              key: item.key,
              children: item.children,
            };
          })}
        />
      </PopoverContent>
    </Popover>
  );
}

function LinkFormattingItem({ format, icon, ...props }) {
  const {
    activeFormats,
    applyFormat,
    getFormatProps,
    removeFormat,
    updateFormat,
  } = useContext(GrigoraFormatsContext);

  const [linkPopoverVisible, setLinkPopoverVisible] = useState(false);
  const [link, setLink] = useState(false);

  function applyUpdateFormat(format, formatProps) {
    if (activeFormats.includes(format)) {
      updateFormat(format, formatProps);
    } else {
      applyFormat(format, formatProps);
    }
  }

  function LinkSettings() {
    return (
      <div>
        <TextInput
          minLength={INPUT_LIMITS["outbound-link"].minlength}
          maxLength={INPUT_LIMITS["outbound-link"].maxlength}
          value={link?.href}
          onChange={(newLink) => {
            const newFormatProps = {
              ...link,
              href: newLink,
            };
            setLink(newFormatProps);
          }}
          label={"Link"}
        />
        <ToggleInput
          value={link?.target === "_blank"}
          onChange={(newLink) => {
            const newFormatProps = {
              ...link,
              target: newLink ? "_blank" : "_self",
            };
            setLink(newFormatProps);
          }}
          label={"Open in New Tab"}
        />
        <TextInput
          minLength={INPUT_LIMITS["link-relation"].minlength}
          maxLength={INPUT_LIMITS["link-relation"].maxlength}
          value={link?.rel}
          onChange={(newRel) => {
            const newFormatProps = {
              ...link,
              rel: newRel,
            };
            setLink(newFormatProps);
          }}
          label={"Rel"}
        />
        <div className="flex gap-[8px]">
          <button
            onClick={(e) => {
              applyUpdateFormat(format, link);
              setLinkPopoverVisible(false);
            }}
            className="text-xs px-2 py-1 rounded-[5px] duration-300 text-white bg-ui-blue hover:bg-ui-blue-dark border"
          >
            Save
          </button>
          {link && (
            <button
              onClick={(e) => {
                removeFormat(format);
                setLinkPopoverVisible(false);
              }}
              className="text-xs px-2 py-1 border rounded-[5px] bg-editor-bg-main"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Popover
      open={linkPopoverVisible}
      onOpenChange={setLinkPopoverVisible}
      placement="left"
    >
      <PopoverAnchor
        onClick={() => {
          if (!linkPopoverVisible) {
            setLink(getFormatProps(format));
          }
          setLinkPopoverVisible(!linkPopoverVisible);
        }}
        title={format}
        className={`richtext-setting-item ${
          activeFormats.includes(format) ? "active" : ""
        } w-[30px] h-[30px] p-1 flex items-center justify-center`}
      >
        <FontAwesomeIcon icon={icon} size="xs" />
      </PopoverAnchor>
      <PopoverContent className="!bg-editor-light-main !z-[1000] !shadow-editor-floating-elements !rounded-[10px] p-4">
        {LinkSettings()}
      </PopoverContent>
    </Popover>
  );
}

function RichTextFormatting() {
  const { editorDocument } = useContext(GrigoraDashboardContext);
  const { id } = useNode((state) => ({
    id: state.id,
  }));
  const [isVisible, setIsVisible] = useState(false);

  const shouldVisible = () => {
    const selection = editorDocument.getSelection();
    // If multiple richtext is selected
    if (isMultipleRichTextSelected(selection)) {
      setIsVisible(false);
      return;
    }

    var currentEditableRichText = selection?.anchorNode?.parentElement.closest(
      ".grigora-richtext-input__field[contenteditable='true']",
    );

    // is not within the contenteditable div
    if (!currentEditableRichText) {
      setIsVisible(false);
      return;
    }

    // If selected contenteditable is inside current block
    if (
      !Array.from(
        editorDocument?.querySelectorAll(`[data-grigora-block-id="${id}"]`) ??
          [],
      )?.some((dom) => dom?.contains(currentEditableRichText))
    ) {
      setIsVisible(false);
      return;
    }
    setIsVisible(true);
  };

  useEffect(() => {
    shouldVisible();
  }, []);

  const selectionActiveFormats = () => {
    shouldVisible();
  };

  useEffect(() => {
    editorDocument?.addEventListener("selectionchange", selectionActiveFormats);
    return () => {
      editorDocument?.removeEventListener(
        "selectionchange",
        selectionActiveFormats,
      );
    };
  }, [editorDocument]);

  if (!isVisible) {
    return;
  }

  return (
    <Panel title={"Rich Text Settings"} initialOpen={false}>
      <div className="flex gap-2 flex-wrap">
        <FormattingItem format={"bold"} icon={faBold} />
        <FormattingItem format={"italic"} icon={faItalic} />
        <FormattingItem format={"highlight"} icon={faHighlighter} />
        <FormattingItem format={"strikethrough"} icon={faStrikethrough} />
        <FormattingItem format={"underline"} icon={faUnderline} />
        <FormattingItem format={"subscript"} icon={faSubscript} />
        <FormattingItem format={"superscript"} icon={faSuperscript} />
        <ColorFormattingItem format={"color"} icon={faPencil} />
        <LinkFormattingItem format={"link"} icon={faLink} />
      </div>
    </Panel>
  );
}

export default RichTextFormatting;
